/**
 * Vocabulario do modulo de leads: como cada evento da linha do tempo vira
 * icone, tom, titulo e detalhe; os rotulos acentuados de estado e situacao;
 * os canais e desfechos de contato.
 *
 * Decisao: a traducao evento -> texto mora AQUI, em funcao pura, e nao dentro
 * do componente da linha do tempo. O componente so desenha; quando o backend
 * mudar um campo, a correcao e num lugar e o `switch` exaustivo (com `never`
 * no default) aponta o tipo que ficou sem texto.
 *
 * Os rotulos de estado do job existem em `lib/format.ts` sem acento
 * (`JOB_STATUS_LABEL`); aqui ficam as versoes acentuadas que o guia pede para
 * texto de interface. Quando o compartilhado for acentuado, este mapa some.
 *
 * `SITUACAO` e a linguagem do lojista (o que aconteceu com a pessoa), nao a
 * do motor (o que aconteceu com o job). Cada situacao tem tom de Badge (para
 * chips e selos) e tom de StatusPill (para a coluna da tabela), porque as
 * duas peças usam paletas diferentes.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatBRLFromCents, formatDuration, formatPercentFromBps, PROVIDER_LABEL, type StatusTone } from '../../lib/format';
import type { FunnelKind, RecoveryJobStatus } from '../../types';
import type { CanalDoFiltro, ContactChannel, DesfechoDoContato, EmailEvent, SituacaoDoLead, TimelineEvent, VoiceCallStatus, WhatsAppEvent } from './types';
import { aoContato } from '../comercial/vendaAssistida';

export const ESTADO_LABEL: Readonly<Record<RecoveryJobStatus, string>> = {
  pending: 'Na fila',
  running: 'Em recuperação',
  succeeded: 'Recuperado',
  aborted: 'Cancelado',
  failed: 'Falhou',
};

export const FUNIL_LABEL: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: 'Carrinho abandonado',
  expirados: 'Expirados',
};

/** O fato que abre a regua, por funil. Toda frase da tela parte dele. */
export const FATO: Readonly<Record<FunnelKind, { readonly verbo: string; readonly substantivo: string; readonly icone: IconName; readonly filaLabel: string }>> = {
  carrinho_abandonado: { verbo: 'Abandonou o checkout', substantivo: 'abandono', icone: 'carrinho', filaLabel: 'fila de 30 minutos' },
  expirados: { verbo: 'Plano venceu', substantivo: 'vencimento', icone: 'calendario', filaLabel: 'espera de 15 dias' },
};

export interface CanalInfo {
  readonly label: string;
  readonly curto: string;
  readonly icon: IconName;
  readonly tone: BadgeTone;
}

export const CANAL: Readonly<Record<ContactChannel, CanalInfo>> = {
  voice: { label: 'Ligação da IA', curto: 'ligação', icon: 'telefone', tone: 'accent' },
  whatsapp: { label: 'WhatsApp', curto: 'WhatsApp', icon: 'whatsapp', tone: 'info' },
  email: { label: 'E-mail', curto: 'e-mail', icon: 'email', tone: 'brand' },
  seller: { label: 'Vendedora', curto: 'vendedora', icon: 'usuarios', tone: 'violet' },
};

export const CANAL_DO_FILTRO: Readonly<Record<CanalDoFiltro, { readonly label: string; readonly icon: IconName }>> = {
  voice: { label: 'Ligação', icon: 'telefone' },
  whatsapp: { label: 'WhatsApp', icon: 'whatsapp' },
  email: { label: 'E-mail', icon: 'email' },
  seller: { label: 'Vendedora', icon: 'usuarios' },
  nenhum: { label: 'Sem contato', icon: 'relogio' },
};

export const ORDEM_DOS_CANAIS: readonly CanalDoFiltro[] = ['voice', 'whatsapp', 'email', 'seller', 'nenhum'];

export interface SituacaoInfo {
  readonly label: string;
  readonly tone: BadgeTone;
  readonly pill: StatusTone;
  /** Uma frase, em termos de negocio. Vira `title` e texto de vazio. */
  readonly descricao: string;
  /** A regua ainda pode disparar algo. */
  readonly viva: boolean;
  /** Ainda sem desfecho de dinheiro (nem pagou, nem cancelou): entra em "em aberto". */
  readonly emAberto: boolean;
}

export const SITUACAO: Readonly<Record<SituacaoDoLead, SituacaoInfo>> = {
  na_fila: { label: 'Na fila', tone: 'neutral', pill: 'neutral', descricao: 'Saiu do checkout (ou o plano venceu) e espera o primeiro toque. Se pagar antes, a régua é cancelada sem custo.', viva: true, emAberto: true },
  em_recuperacao: { label: 'Em recuperação', tone: 'info', pill: 'neutral', descricao: 'A régua está andando: já houve toque e ainda há toque agendado.', viva: true, emAberto: true },
  com_comercial: { label: 'Com o comercial', tone: 'violet', pill: 'neutral', descricao: 'Respondeu e a vendedora assumiu. A régua para; se a venda sair em até 7 dias do último toque, a comissão é desse toque e a vendedora fica como “fechado por”.', viva: false, emAberto: true },
  recuperado: { label: 'Recuperado', tone: 'good', pill: 'good', descricao: 'Pagou depois de um contato, dentro de 7 dias. Comissão creditada ao último contato (ADR-001).', viva: false, emAberto: false },
  pagou_sozinho: { label: 'Pagou sozinho', tone: 'neutral', pill: 'warning', descricao: 'Pagou sem gerar comissão: antes do primeiro toque ou mais de 7 dias depois do último contato. A régua foi cancelada.', viva: false, emAberto: false },
  esgotado: { label: 'Esgotado', tone: 'warning', pill: 'serious', descricao: 'Passou por todos os toques da régua sem voltar. Ainda pode ser recuperado por um contato humano.', viva: false, emAberto: true },
  falhou: { label: 'Falhou', tone: 'critical', pill: 'critical', descricao: 'O motor não conseguiu concluir a régua (canal fora do ar, teto de resgates). Veja a linha do tempo.', viva: false, emAberto: true },
  cancelado: { label: 'Cancelado', tone: 'neutral', pill: 'warning', descricao: 'Removido da régua pelo painel ou conversão revertida por chargeback.', viva: false, emAberto: false },
};

export const DESFECHO_LABEL: Readonly<Record<DesfechoDoContato, string>> = {
  answered: 'atendeu',
  voicemail: 'caixa postal',
  no_answer: 'não atendeu',
  failed: 'falhou',
  sent: 'enviado',
  delivered: 'entregue',
  read: 'lido',
  clicked: 'clicou no botão',
  opened: 'abriu',
  replied: 'respondeu',
  took_over: 'vendedora assumiu',
  sale: 'venda fechada',
};

const STATUS_LIGACAO: Readonly<Record<VoiceCallStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  answered: { label: 'atendida', tone: 'good' },
  voicemail: { label: 'caixa postal', tone: 'warning' },
  no_answer: { label: 'sem resposta', tone: 'warning' },
  failed: { label: 'falhou', tone: 'critical' },
};

/** Classes do circulo do icone na linha do tempo, por tom (tokens do guia). */
export const TOM_DO_ICONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'border-line bg-surface text-ink-muted',
  brand: 'border-brand/40 bg-brand/10 text-brand',
  accent: 'border-accent/40 bg-accent/10 text-accent',
  info: 'border-info/40 bg-info/10 text-info',
  good: 'border-status-good/40 bg-status-good/10 text-status-good',
  warning: 'border-status-warning/50 bg-status-warning/10 text-status-warning',
  critical: 'border-status-critical/40 bg-status-critical/10 text-status-critical',
  violet: 'border-series-7/40 bg-series-7/10 text-series-7',
};

/** Categoria de evento para o filtro da linha do tempo. */
export type CategoriaDoEvento = 'voz' | 'whatsapp' | 'email' | 'pessoas' | 'dinheiro' | 'motor';

export const CATEGORIA: Readonly<Record<CategoriaDoEvento, { readonly label: string; readonly icon: IconName }>> = {
  voz: { label: 'Ligações', icon: 'telefone' },
  whatsapp: { label: 'WhatsApp', icon: 'whatsapp' },
  email: { label: 'E-mail', icon: 'email' },
  pessoas: { label: 'Lead e comercial', icon: 'usuarios' },
  dinheiro: { label: 'Pagamentos', icon: 'moeda' },
  motor: { label: 'Motor', icon: 'robo' },
};

export const ORDEM_DAS_CATEGORIAS: readonly CategoriaDoEvento[] = ['voz', 'whatsapp', 'email', 'pessoas', 'dinheiro', 'motor'];

export function categoriaDoEvento(event: TimelineEvent): CategoriaDoEvento {
  switch (event.kind) {
    case 'voice_call':
      return 'voz';
    case 'whatsapp':
      return 'whatsapp';
    case 'email':
      return 'email';
    case 'lead_reply':
    case 'seller_took_over':
      return 'pessoas';
    case 'payment_approved':
    case 'reversal':
    case 'seller_sale':
      return 'dinheiro';
    case 'webhook_received':
    case 'queued':
    case 'rule_paused':
    case 'rule_removed':
    case 'job_failed':
    case 'rule_ended':
      return 'motor';
    default: {
      const nunca: never = event;
      return nunca;
    }
  }
}

export interface DescricaoDoEvento {
  readonly icon: IconName;
  readonly tone: BadgeTone;
  readonly title: string;
  readonly detail: string | null;
}

function ordinal(n: number): string {
  return `${n}ª`;
}

export function descreverEvento(event: TimelineEvent): DescricaoDoEvento {
  switch (event.kind) {
    case 'webhook_received':
      return {
        icon: 'raio',
        tone: 'accent',
        title: `${PROVIDER_LABEL[event.provider]} avisou: ${event.eventLabel}`,
        detail:
          event.eventLabel === 'assinatura vencida'
            ? `Plano de ${formatBRLFromCents(event.amountCents)} chegou ao fim sem renovação · assinatura ${event.externalId}`
            : event.skipReason === 'ja_pago'
              ? `Checkout de ${formatBRLFromCents(event.amountCents)} · pedido ${event.externalId} · o aviso chegou depois do pagamento: conta no funil, mas a régua não foi agendada e ninguém ligou.`
              : `Checkout de ${formatBRLFromCents(event.amountCents)} não concluído · pedido ${event.externalId}`,
      };
    case 'queued':
      return {
        icon: 'relogio',
        tone: 'neutral',
        title: 'Entrou na fila de recuperação',
        detail:
          event.delayMinutes >= 1440
            ? `Primeiro toque agendado para ${Math.round(event.delayMinutes / 1440)} dias depois do vencimento. Se a pessoa voltar a pagar antes, a régua é cancelada (RF05).`
            : `Ligação da IA agendada para ${event.delayMinutes} minutos depois do abandono. Se o pagamento chegar antes, a régua é cancelada (RF05).`,
      };
    case 'voice_call': {
      const status = STATUS_LIGACAO[event.status];
      const duracao = event.durationSeconds === null ? null : `Duração ${formatDuration(event.durationSeconds)}`;
      const partes = [duracao, event.summary].filter((p): p is string => p !== null);
      return {
        icon: 'telefone',
        tone: status.tone,
        title: `Ligação da IA · ${ordinal(event.attempt)} tentativa de ${event.maxAttempts} — ${status.label}`,
        detail: partes.length === 0 ? null : partes.join(' · '),
      };
    }
    case 'whatsapp':
      return descreverWhatsApp(event);
    case 'email':
      return descreverEmail(event);
    case 'lead_reply':
      return {
        icon: 'resposta',
        tone: 'violet',
        title: `Lead respondeu pelo ${CANAL[event.channel].label}`,
        detail: null,
      };
    case 'seller_took_over':
      return {
        icon: 'usuarios',
        tone: 'violet',
        title: `${event.sellerName} assumiu a conversa`,
        detail: 'A régua automática para aqui. Se a venda sair em até 7 dias do último toque, a comissão continua creditada a esse toque; a vendedora fica como “fechado por” (ADR-001 E5).',
      };
    case 'payment_approved':
      return {
        icon: 'moeda',
        tone: 'good',
        title: `Pagamento aprovado · ${formatBRLFromCents(event.amountCents)}`,
        detail:
          event.attribution === 'ja_creditado_por_venda_assistida'
            ? `${PROVIDER_LABEL[event.provider]} confirmou o pagamento de um pedido já creditado pela venda assistida: régua parada (RF05), sem segunda comissão (ADR-001 E5).`
            : event.creditedTo === null
              ? `${PROVIDER_LABEL[event.provider]} confirmou o pagamento antes de qualquer contato: régua cancelada (RF05) e sem comissão (ADR-001).`
              : `Creditado ao contato: ${CANAL[event.creditedTo.channel].label} ${event.creditedTo.touchLabel} · comissão ${formatBRLFromCents(event.commissionCents)} (${formatPercentFromBps(event.feeBpsApplied)} do valor pago).`,
      };
    case 'reversal':
      return {
        icon: 'alerta',
        tone: 'critical',
        title: `${event.reason === 'refund' ? 'Reembolso' : 'Chargeback'} · ${formatBRLFromCents(event.amountCents)}`,
        detail:
          event.commissionRevertedCents > 0
            ? `Conversão revertida; comissão de ${formatBRLFromCents(event.commissionRevertedCents)} devolvida (política da empresa, ADR-001).`
            : event.reason === 'refund'
              ? 'Reembolso não reverte comissão (política padrão da empresa); aqui não havia comissão a reverter.'
              : 'Não havia comissão a reverter.',
      };
    case 'seller_sale':
      return {
        icon: event.outcome === 'creditado' ? 'estrela' : event.outcome === 'creditado_revertido' ? 'alerta' : 'info',
        tone: event.outcome === 'creditado' ? 'good' : event.outcome === 'creditado_revertido' ? 'warning' : 'neutral',
        title: `Venda assistida · ${event.sellerName} fechou ${formatBRLFromCents(event.amountCents)}`,
        detail: `${
          event.outcome === 'creditado'
            ? `Pedido ${event.orderRef} · comissão ${formatBRLFromCents(event.commissionCents)} (${formatPercentFromBps(event.feeBpsApplied)}) creditada ${aoContato(event.creditedTouch ?? 'último toque da régua')}; a vendedora fica como “fechado por” (ADR-001 E5).`
            : event.outcome === 'creditado_revertido'
              ? `Pedido ${event.orderRef} · creditada ${aoContato(event.creditedTouch ?? 'último toque da régua')} e revertida no mesmo ato: já havia estorno para esta referência e a política da empresa o reverte.`
              : event.outcome === 'ja_creditado_por_webhook'
                ? `Pedido ${event.orderRef} · o mesmo pedido já tinha sido creditado pelo webhook da plataforma: registrada no histórico, sem segunda comissão (ADR-001 E5).`
                : `Pedido ${event.orderRef} · sem toque da régua nos 7 dias antes do pagamento: registrada no histórico, sem comissão (ADR-001).`
        }${event.registeredByName !== undefined && event.registeredByName !== null && event.registeredByName !== event.sellerName ? ` Lançada no painel por ${event.registeredByName}.` : ''}`,
      };
    case 'rule_paused':
      return {
        icon: event.paused ? 'pausa' : 'play',
        tone: 'warning',
        title: event.paused ? 'Régua pausada' : 'Régua retomada',
        detail: null,
      };
    case 'rule_removed':
      return { icon: 'x', tone: 'warning', title: 'Removido da régua', detail: event.reason };
    case 'job_failed':
      return { icon: 'alerta', tone: 'critical', title: 'Régua encerrada pelo motor', detail: event.lastError };
    case 'rule_ended':
      return {
        icon: 'escudo',
        tone: 'warning',
        title: event.reason === 'descadastrou' ? 'Pediu para sair da régua' : 'Régua chegou ao fim sem resposta',
        detail:
          event.reason === 'descadastrou'
            ? `Nenhum toque a mais. Recebeu ${event.touches} ${event.touches === 1 ? 'toque' : 'toques'} antes de pedir para sair.`
            : `Passou por ${event.touches} ${event.touches === 1 ? 'toque' : 'toques'} sem pagar nem responder. O lead continua no histórico; um contato humano ainda pode recuperá-lo.`,
      };
    default: {
      // Exaustividade: um `kind` novo no backend cai aqui em tempo de compilacao.
      const nunca: never = event;
      return nunca;
    }
  }
}

function descreverWhatsApp(event: WhatsAppEvent): DescricaoDoEvento {
  switch (event.stage) {
    case 'sent':
      return {
        icon: 'whatsapp',
        tone: 'info',
        title: `WhatsApp ${event.touchLabel} enviado`,
        detail: event.buttonLabel === null ? null : `Com o botão "${event.buttonLabel}"`,
      };
    case 'delivered':
      return { icon: 'whatsapp', tone: 'info', title: 'Entregue no aparelho do lead', detail: null };
    case 'read':
      return { icon: 'olho', tone: 'info', title: 'Lido pelo lead', detail: null };
    case 'clicked':
      return {
        icon: 'clique',
        tone: 'good',
        title: `Clicou no botão${event.buttonLabel === null ? '' : ` "${event.buttonLabel}"`}`,
        detail: 'Abriu o checkout pelo link da régua. Clicar não tira ninguém da fila: só o pagamento tira.',
      };
    case 'failed':
      return {
        icon: 'alerta',
        tone: 'critical',
        title: `WhatsApp ${event.touchLabel} falhou`,
        detail: event.errorMessage,
      };
    default: {
      const nunca: never = event.stage;
      return nunca;
    }
  }
}

function descreverEmail(event: EmailEvent): DescricaoDoEvento {
  switch (event.stage) {
    case 'sent':
      return {
        icon: 'email',
        tone: 'brand',
        title: `E-mail ${event.touchLabel} enviado${event.fallback ? ' (reserva do WhatsApp)' : ''}`,
        detail: `Assunto: ${event.subject}`,
      };
    case 'opened':
      return { icon: 'olho', tone: 'brand', title: 'E-mail aberto', detail: null };
    case 'clicked':
      return { icon: 'clique', tone: 'good', title: 'Clicou no link do e-mail', detail: null };
    default: {
      const nunca: never = event.stage;
      return nunca;
    }
  }
}

/**
 * "há 12 min", "em 18 min", "há 2 h", "em 3 dias". Recebe o "agora" de fora
 * porque na previa o relogio e `AGORA_SIMULADO`, nao o do navegador.
 */
export function descreverRelativo(iso: string, agora: Date): string {
  const instante = new Date(iso).getTime();
  if (Number.isNaN(instante)) {
    return '';
  }
  const diffMin = Math.round((instante - agora.getTime()) / 60_000);
  const passado = diffMin < 0;
  const abs = Math.abs(diffMin);
  if (abs < 1) {
    return 'agora';
  }
  return passado ? `há ${duracaoCurta(abs)}` : `em ${duracaoCurta(abs)}`;
}

/** Minutos -> "12 min", "2 h 05 min", "3 dias". Sem "há/em": para colunas e frases que ja dizem a direcao. */
export function duracaoCurta(minutos: number): string {
  const abs = Math.max(0, Math.round(minutos));
  if (abs < 60) {
    return `${abs} min`;
  }
  if (abs < 60 * 24) {
    const horas = Math.floor(abs / 60);
    const resto = abs % 60;
    return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
  }
  const dias = Math.round(abs / (60 * 24));
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

/**
 * Prazo que se ESGOTA (janela de credito): sempre arredonda para BAIXO e,
 * abaixo de 2 dias, fala em horas. `duracaoCurta` arredonda para o mais
 * proximo e serve para duracao ja passada; num prazo, 36 h viravam "2 dias"
 * e a vendedora que fechasse com 40 h perdia a comissao achando que estava
 * no prazo. Aqui nunca se promete mais tempo do que existe.
 */
export function prazoRestante(minutos: number): string {
  const m = Math.max(0, Math.floor(minutos));
  if (m < 60) {
    return `${m} min`;
  }
  if (m < 2 * 1440) {
    const horas = Math.floor(m / 60);
    const resto = m % 60;
    return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
  }
  const dias = Math.floor(m / 1440);
  const horas = Math.floor((m % 1440) / 60);
  return horas === 0 ? `${dias} dias` : `${dias} dias e ${horas} h`;
}

/** Minutos desde o abandono -> "T+30min", "T+1h", "T+1h30". Mesmo formato da regua do funil de carrinho. */
export function rotuloDoAtraso(minutes: number): string {
  if (minutes < 60) {
    return `T+${minutes}min`;
  }
  if (minutes % 60 === 0) {
    return `T+${minutes / 60}h`;
  }
  return `T+${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;
}

/** "T+…" no carrinho, "D+…" nos expirados (mesmo vocabulario das paginas de funil). */
export function rotuloDoAtrasoPorFunil(funnel: FunnelKind, minutes: number): string {
  if (funnel === 'expirados') {
    return `D+${Math.max(1, Math.round(minutes / 1440))}`;
  }
  return rotuloDoAtraso(Math.max(0, Math.round(minutes)));
}

/**
 * Posicao de um instante em relacao ao fato ("T+2h49", "D+16"), para a linha
 * do tempo mostrar que TUDO veio depois do abandono/vencimento. Antes do fato
 * devolve "T0" (o proprio fato) -- nada legitimo acontece antes dele.
 */
export function posicaoRelativaAoFato(funnel: FunnelKind, fatoIso: string, iso: string): string {
  const diff = Math.round((new Date(iso).getTime() - new Date(fatoIso).getTime()) / 60_000);
  if (diff <= 0) {
    return 'T0';
  }
  if (funnel === 'expirados') {
    return diff < 1440 ? `D0 +${duracaoCurta(diff)}` : `D+${Math.floor(diff / 1440)}`;
  }
  if (diff >= 1440) {
    const dias = Math.floor(diff / 1440);
    const horas = Math.floor((diff % 1440) / 60);
    return horas === 0 ? `T+${dias}d` : `T+${dias}d${horas}h`;
  }
  return rotuloDoAtraso(diff);
}

const DIA_DA_SEMANA = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' });
const DIA_COMPLETO = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

/** "Hoje", "Ontem" ou "quarta-feira, 22 de setembro de 2026", relativo ao "agora" da tela. */
export function rotuloDoDia(iso: string, agora: Date): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) {
    return 'Data desconhecida';
  }
  const chaveData = chaveDoDia(data);
  const chaveHoje = chaveDoDia(agora);
  if (chaveData === chaveHoje) {
    return 'Hoje';
  }
  const ontem = new Date(agora);
  ontem.setDate(ontem.getDate() - 1);
  if (chaveData === chaveDoDia(ontem)) {
    return 'Ontem';
  }
  return `${DIA_DA_SEMANA.format(data)}, ${DIA_COMPLETO.format(data)}`;
}

/** YYYY-MM-DD no fuso do navegador: e por este valor que a linha do tempo agrupa. */
export function chaveDoDia(data: Date): string {
  const y = data.getFullYear();
  const m = String(data.getMonth() + 1).padStart(2, '0');
  const d = String(data.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
